import { z } from 'zod';
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import { parseOrWarn } from '@/lib/zod-resolver';
import { paginationSchema } from './_types/common';
import type { Pagination } from './_types/common';

/**
 * 申报案 API（v4 M3-ρ-3）— 全 10 endpoint + 7 态状态机封装。
 *
 * 后端契约源：backend_app/app/api/v1/declaration_cases.py
 *   GET    /declaration-cases?subject_id?&policy_pack_id?&status?&declare_year?&page&page_size
 *   GET    /declaration-cases/{case_id}
 *   POST   /declaration-cases                              body: DeclarationCaseCreate
 *   PATCH  /declaration-cases/{case_id}                    body: DeclarationCaseUpdate
 *   POST   /declaration-cases/{case_id}/freeze             body: FreezeBody
 *   POST   /declaration-cases/{case_id}/submit             body: SubmitBody
 *   POST   /declaration-cases/{case_id}/start-review       body: StartReviewBody
 *   POST   /declaration-cases/{case_id}/record-review      body: RecordReviewBody
 *   POST   /declaration-cases/{case_id}/withdraw           body: WithdrawBody
 *   DELETE /declaration-cases/{case_id}
 *
 * 状态机 7 态（schemas/declaration_case.py:_STATUS）：
 *   draft → frozen → submitted → reviewing → approved/rejected
 *   任意非终止态 → withdrawn
 *   终止态：approved / rejected / withdrawn （不可写）
 *
 * 字段对位 DeclarationCaseOut（schemas/declaration_case.py:110-132）— 22 字段。
 */

// === 状态枚举（与后端 _STATUS Literal 一一对位） ===
export const caseStatusSchema = z.enum([
  'draft',
  'frozen',
  'submitted',
  'reviewing',
  'approved',
  'rejected',
  'withdrawn',
]);
export type CaseStatus = z.infer<typeof caseStatusSchema>;

export const TERMINAL_STATUSES: ReadonlySet<CaseStatus> = new Set([
  'approved',
  'rejected',
  'withdrawn',
]);

export function isTerminal(status: CaseStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** 7 态有序列（Kanban 列固定顺序） */
export const CASE_STATUS_ORDER: readonly CaseStatus[] = [
  'draft',
  'frozen',
  'submitted',
  'reviewing',
  'approved',
  'rejected',
  'withdrawn',
] as const;

// === DeclarationCaseOut（22 字段对位） ===
// 注：working_state / frozen_snapshot 是 jsonb，不做内层 schema 约束，全用 record(unknown)
// 时间戳后端 isoformat() 字符串；null 显式 nullable
export const declarationCaseSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  subject_id: z.string(),
  policy_pack_id: z.string(),
  // Sprint19-fix:BE batch-lookup 注入名称(Kanban 卡片显示用,旧记录 fallback 用 shortId)
  subject_name: z.string().nullable().optional(),
  policy_pack_name: z.string().nullable().optional(),
  // S20-C0:负责人(owner)账号 — BE list/get 接口在 enrichment 阶段附带 owner_name
  owner_account_id: z.string().nullable().optional(),
  owner_name: z.string().nullable().optional(),
  // Sprint20-extra-hotfix3:申报资质类型(从 policy_pack.grants_qualification_id 链 catalog.name)
  qualification_name: z.string().nullable().optional(),
  // Sprint20-extra (multi-qual seam):case 级"本案申报的资质"(用户在创建 modal 选)
  // declared_qualification_name = catalog.name 的二级 enrichment; null 则 fallback 到 qualification_name
  declared_qualification_id: z.string().nullable().optional(),
  declared_qualification_name: z.string().nullable().optional(),
  declare_year: z.number().int().nullable(),
  batch_code: z.string().nullable(),
  status: caseStatusSchema,
  working_state: z.record(z.unknown()),
  frozen_snapshot: z.record(z.unknown()).nullable(),
  frozen_at: z.string().nullable(),
  frozen_by_account_id: z.string().nullable(),
  frozen_policy_pack_version: z.string().nullable(),
  submitted_at: z.string().nullable(),
  gov_receipt_no: z.string().nullable(),
  review_result_at: z.string().nullable(),
  review_result: z.string().nullable(),
  review_comments: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
  created_by_account_id: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type DeclarationCase = z.infer<typeof declarationCaseSchema>;

export const declarationCaseListSchema = paginationSchema(declarationCaseSchema);

// === 请求 body 类型（对位 schemas/declaration_case.py） ===

/** DeclarationCaseCreate — declare_year 与 batch_code 至少需提供一个（后端 model_validator 守） */
export interface CreateCaseBody {
  subject_id: string;
  policy_pack_id: string;
  declare_year?: number | null;
  batch_code?: string | null;
  working_state?: Record<string, unknown>;
  /** S20-C0:可选指派 owner — 若不传,后端自动建议为 enterprise primary staff */
  owner_account_id?: string | null;
  /** Sprint20-extra:本案申报的资质(多资质政策包中由用户在 Modal 选);0/1 候选时可省略 */
  declared_qualification_id?: string | null;
}

/** DeclarationCaseUpdate — 仅 draft/frozen 可 PATCH；不含 status（走状态机端点） */
export interface UpdateCaseBody {
  declare_year?: number | null;
  batch_code?: string | null;
  working_state?: Record<string, unknown> | null;
  gov_receipt_no?: string | null;
  review_comments?: string | null;
}

/** FreezeBody / StartReviewBody — 仅含可选 note */
export interface NoteBody {
  note?: string;
}

/** SubmitBody — 含可选 gov_receipt_no */
export interface SubmitBody {
  gov_receipt_no?: string;
}

/** RecordReviewBody — result + 可选 review_comments；result 限定 approved/rejected */
export interface RecordReviewBody {
  result: 'approved' | 'rejected';
  review_comments?: string;
}

/** WithdrawBody — 仅含可选 reason */
export interface WithdrawBody {
  reason?: string;
}

// === 查询参数 ===
export interface ListCasesParams {
  subject_id?: string;
  policy_pack_id?: string;
  status?: CaseStatus;
  declare_year?: number;
  page?: number;
  page_size?: number;
}

// === 工具 — encodeURIComponent（M3-ρ-1 SR-med#2 教训） ===
function _safe(seg: string): string {
  return encodeURIComponent(seg);
}

// === API calls ===

/** 列表（Kanban 主拉取）— 默认 page=1, page_size=200 一次拉满分列分组 */
export async function listCases(
  params: ListCasesParams = {},
): Promise<Pagination<DeclarationCase>> {
  const q: Record<string, unknown> = {
    page: params.page ?? 1,
    page_size: params.page_size ?? 200,
  };
  if (params.subject_id) q.subject_id = params.subject_id;
  if (params.policy_pack_id) q.policy_pack_id = params.policy_pack_id;
  if (params.status) q.status = params.status;
  if (params.declare_year != null) q.declare_year = params.declare_year;

  const raw = await apiGet('/declaration-cases', q);
  return parseOrWarn(
    declarationCaseListSchema,
    raw,
    'GET /declaration-cases',
  );
}

export async function getCase(caseId: string): Promise<DeclarationCase> {
  const raw = await apiGet(`/declaration-cases/${_safe(caseId)}`);
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `GET /declaration-cases/${caseId}`,
  );
}

export async function createCase(body: CreateCaseBody): Promise<DeclarationCase> {
  const raw = await apiPost('/declaration-cases', body);
  return parseOrWarn(declarationCaseSchema, raw, 'POST /declaration-cases');
}

export async function patchCase(
  caseId: string,
  body: UpdateCaseBody,
): Promise<DeclarationCase> {
  const raw = await apiPatch(`/declaration-cases/${_safe(caseId)}`, body);
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `PATCH /declaration-cases/${caseId}`,
  );
}

export async function freezeCase(
  caseId: string,
  body: NoteBody = {},
): Promise<DeclarationCase> {
  const raw = await apiPost(`/declaration-cases/${_safe(caseId)}/freeze`, body);
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `POST /declaration-cases/${caseId}/freeze`,
  );
}

export async function submitCase(
  caseId: string,
  body: SubmitBody = {},
): Promise<DeclarationCase> {
  const raw = await apiPost(`/declaration-cases/${_safe(caseId)}/submit`, body);
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `POST /declaration-cases/${caseId}/submit`,
  );
}

export async function startReview(
  caseId: string,
  body: NoteBody = {},
): Promise<DeclarationCase> {
  const raw = await apiPost(
    `/declaration-cases/${_safe(caseId)}/start-review`,
    body,
  );
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `POST /declaration-cases/${caseId}/start-review`,
  );
}

export async function recordReview(
  caseId: string,
  body: RecordReviewBody,
): Promise<DeclarationCase> {
  const raw = await apiPost(
    `/declaration-cases/${_safe(caseId)}/record-review`,
    body,
  );
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `POST /declaration-cases/${caseId}/record-review`,
  );
}

export async function withdrawCase(
  caseId: string,
  body: WithdrawBody = {},
): Promise<DeclarationCase> {
  const raw = await apiPost(
    `/declaration-cases/${_safe(caseId)}/withdraw`,
    body,
  );
  return parseOrWarn(
    declarationCaseSchema,
    raw,
    `POST /declaration-cases/${caseId}/withdraw`,
  );
}

export async function deleteCase(caseId: string): Promise<{ id: string }> {
  const raw = await apiDelete<{ id: string }>(
    `/declaration-cases/${_safe(caseId)}`,
  );
  return raw;
}

// ============================================================================
// 案工作台 / 申报看板共用 — 指标红绿灯 view-model
//
// Sprint 36: 老 endpoint GET /declaration-cases/{id}/indicators 已删
//  (不读 case_indicator_binding,导致 3 套口径)。
// 现统一走 GET /monitoring/compliance?case_id=... → IndicatorEvalNode,
// 用 evalNodeToCaseIndicator() 适配成本视图模型。
// ============================================================================

export const indicatorLightSchema = z.enum(['green', 'yellow', 'red']);
export type IndicatorLight = z.infer<typeof indicatorLightSchema>;

export interface CaseIndicator {
  code: string;
  name: string;
  current_value: number | null;
  target_value: number | null;
  unit: string;
  light: IndicatorLight;
  gap: string;
  source: string;
}

/** monitoring IndicatorEvalNode → CaseIndicator 视图模型 */
export function evalNodeToCaseIndicator(
  node: import('./monitoring').IndicatorEvalNode,
): CaseIndicator {
  const light: IndicatorLight =
    node.passed === true ? 'green' : node.passed === false ? 'red' : 'yellow';
  const toNum = (v: unknown): number | null =>
    typeof v === 'number'
      ? v
      : typeof v === 'string' && !Number.isNaN(Number(v))
        ? Number(v)
        : null;
  return {
    code: node.code,
    name: node.name,
    current_value: toNum(node.actual),
    target_value: toNum(node.required),
    unit: '',
    light,
    gap:
      node.passed === true
        ? '已达标'
        : node.passed === false
          ? '未达标'
          : node.note || '待人工确认',
    source: node.note || '案级 binding + 引擎实时',
  };
}

export const caseTimelineEventSchema = z.object({
  id: z.string(),
  action: z.string(),
  action_label: z.string(),
  actor_account_id: z.string().nullable(),
  actor_role: z.string().nullable(),
  entity_type: z.string(),
  entity_id: z.string().nullable(),
  after_snapshot: z.record(z.unknown()).nullable(),
  created_at: z.string().nullable(),
});
export type CaseTimelineEvent = z.infer<typeof caseTimelineEventSchema>;

export const caseTimelineResponseSchema = z.object({
  items: z.array(caseTimelineEventSchema),
  total: z.number(),
});
export type CaseTimelineResponse = z.infer<typeof caseTimelineResponseSchema>;

/** S20-C1 — GET /declaration-cases/{id}/timeline */
export async function getCaseTimeline(
  caseId: string,
): Promise<CaseTimelineResponse> {
  const raw = await apiGet(`/declaration-cases/${_safe(caseId)}/timeline`);
  return parseOrWarn(
    caseTimelineResponseSchema,
    raw,
    `GET /declaration-cases/${caseId}/timeline`,
  );
}

// === UI 辅助 ===

/** 7 态显示标签 */
export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  draft: '草稿',
  frozen: '冻结',
  submitted: '已提交',
  reviewing: '审查中',
  approved: '通过',
  rejected: '驳回',
  withdrawn: '撤回',
};

/**
 * 7 态色彩 token 映射（与 tokens.css 对位）。
 * 0 hex 硬编码 — 全部走 CSS variable。
 */
export interface StatusVisual {
  /** 卡片左色条 / chip 背景 */
  bg: string;
  /** chip 主色文字 */
  fg: string;
  /** 边框（淡色） */
  border: string;
}

// ============================================================================
// S20-C2:案工作台 Tab2 材料清单聚合 — GET /declaration-cases/{case_id}/materials
// 后端契约源:backend_app/app/api/v1/material_links.py list_case_materials
// ============================================================================

const caseMaterialItemSchema = z.object({
  id: z.string(),
  material_type_code: z.string(),
  material_type_name: z.string(),
  display_name: z.string(),
  status: z.enum(['uploaded', 'parsed', 'verified', 'expired', 'invalid']),
  file_url: z.string().nullable(),
  mime_type: z.string().nullable(),
  file_size: z.number().nullable(),
  uploaded_at: z.string().nullable(),
  verified_at: z.string().nullable(),
  linked_indicators: z.array(z.string()),
  uploaded_by_account_id: z.string().nullable(),
  uploaded_by_username: z.string().nullable(),
});
export type CaseMaterialItem = z.infer<typeof caseMaterialItemSchema>;

const caseMissingMaterialSchema = z.object({
  material_type_code: z.string(),
  material_type_name: z.string(),
  required_by_indicators: z.array(z.string()),
  input_route: z.string(),
});
export type CaseMissingMaterial = z.infer<typeof caseMissingMaterialSchema>;

const caseMaterialsSummarySchema = z.object({
  total: z.number(),
  verified: z.number(),
  uploaded: z.number(),
  missing: z.number(),
});

export const caseMaterialsBundleSchema = z.object({
  items: z.array(caseMaterialItemSchema),
  required_missing: z.array(caseMissingMaterialSchema),
  summary: caseMaterialsSummarySchema,
});
export type CaseMaterialsBundle = z.infer<typeof caseMaterialsBundleSchema>;

export async function getCaseMaterials(caseId: string): Promise<CaseMaterialsBundle> {
  const raw = await apiGet(`/declaration-cases/${_safe(caseId)}/materials`);
  return parseOrWarn(
    caseMaterialsBundleSchema,
    raw,
    `GET /declaration-cases/${caseId}/materials`,
  );
}

export const CASE_STATUS_VISUAL: Record<CaseStatus, StatusVisual> = {
  draft: {
    bg: 'var(--color-info-bg)',
    fg: 'var(--color-info)',
    border: 'var(--color-info-3)',
  },
  frozen: {
    bg: 'var(--color-primary-bg)',
    fg: 'var(--color-primary)',
    border: 'var(--color-primary-hover)',
  },
  submitted: {
    bg: 'var(--color-warning-bg)',
    fg: 'var(--color-warning)',
    border: 'var(--color-warning-2)',
  },
  reviewing: {
    bg: 'var(--color-info-bg)',
    fg: 'var(--color-info-2)',
    border: 'var(--color-info-3)',
  },
  approved: {
    bg: 'var(--color-success-bg)',
    fg: 'var(--color-success)',
    border: 'var(--color-success-2)',
  },
  rejected: {
    bg: 'var(--color-danger-bg-alt)',
    fg: 'var(--color-danger-2)',
    border: 'var(--color-danger-3)',
  },
  withdrawn: {
    bg: 'var(--color-bg-elevated)',
    fg: 'var(--color-text-tertiary)',
    border: 'var(--color-border-soft)',
  },
};
